import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, Zap, CheckCircle2 } from "lucide-react";

const METRICS = ["PRICE", "SPEED", "SERVICEABILITY", "DELIVERY PERFORMANCE", "COD", "RTO RISK"];
const COURIERS = ["Courier A", "Courier B", "Courier C", "Courier D", "Courier E"];
const CYCLE = [2, 4, 1, 3, 0]; // rotating "recommended" courier for the live demo feel

export default function CourierEngine() {
  const [metricIdx, setMetricIdx] = useState(0);
  const [pick, setPick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setMetricIdx((m) => {
      const next = (m + 1) % (METRICS.length + 1);
      if (next === 0) setPick((p) => (p + 1) % COURIERS.length);
      return next;
    }), 1100);
    return () => clearInterval(t);
  }, []);

  const analyzing = metricIdx < METRICS.length;
  const recommended = analyzing ? -1 : CYCLE[pick];

  return (
    <section className="relative overflow-hidden bg-[#030B18] py-20 sm:py-24" data-testid="smart-courier-engine">
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-40" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2">
        {/* Routing visualization */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
          className="relative rounded-3xl border border-white/10 bg-[#0B1E3D]/60 p-5 backdrop-blur-xl sm:p-7" data-testid="engine-visual">
          <div className="mb-4 flex flex-wrap gap-1.5">
            {METRICS.map((m, i) => (
              <span key={m} className={`mono rounded-full px-2.5 py-1 text-[9px] font-bold tracking-wider transition-all duration-300 ${
                analyzing && i === metricIdx ? "bg-[#FF9933] text-[#071B3A] shadow-[0_0_16px_rgba(255,153,51,0.6)]"
                : analyzing && i < metricIdx ? "bg-[#10B981]/20 text-[#10B981]"
                : "bg-white/5 text-slate-500"}`} data-testid={`engine-metric-${m.toLowerCase().replace(/\s+/g, "-")}`}>{m}</span>
            ))}
          </div>
          <svg viewBox="0 0 600 400" className="w-full">
            <defs>
              <linearGradient id="engineGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FF9933" /><stop offset="100%" stopColor="#10B981" />
              </linearGradient>
            </defs>
            {/* source -> engine */}
            <path d="M 70 200 L 210 200" stroke="rgba(18,200,245,0.5)" strokeWidth="2" className="route-dash-fast" fill="none" />
            {/* engine -> couriers */}
            {COURIERS.map((_, i) => {
              const y = 52 + i * 74;
              const active = recommended === i;
              return (
                <g key={i}>
                  <path d={`M 300 200 C 370 200, 390 ${y}, 470 ${y}`} fill="none"
                    stroke={active ? "url(#engineGrad)" : "rgba(148,163,184,0.25)"}
                    strokeWidth={active ? 3 : 1.5} className={active ? "route-dash-fast" : "route-dash"}
                    style={active ? { filter: "drop-shadow(0 0 8px rgba(16,185,129,0.8))" } : undefined} />
                  <rect x="470" y={y - 22} width="118" height="44" rx="12"
                    fill={active ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.04)"}
                    stroke={active ? "#10B981" : "rgba(255,255,255,0.12)"} strokeWidth="1.5" />
                  <text x="529" y={y + 4} textAnchor="middle" fill={active ? "#10B981" : "#94A3B8"} fontSize="13" fontWeight="700" fontFamily="Inter">{COURIERS[i]}</text>
                  {active && <circle cx="470" cy={y} r="5" fill="#10B981"><animate attributeName="opacity" values="1;0.2;1" dur="1s" repeatCount="indefinite" /></circle>}
                </g>
              );
            })}
            {/* source node */}
            <rect x="18" y="176" width="104" height="48" rx="12" fill="rgba(255,153,51,0.12)" stroke="#FF9933" strokeWidth="1.5" />
            <text x="70" y="204" textAnchor="middle" fill="#FFB25E" fontSize="13" fontWeight="700" fontFamily="Inter">Shipment</text>
            {/* engine node */}
            <circle cx="255" cy="200" r="46" fill="rgba(7,27,58,0.95)" stroke="#12C8F5" strokeWidth="2" />
            <circle cx="255" cy="200" r="58" fill="none" stroke="rgba(18,200,245,0.3)" strokeWidth="1" strokeDasharray="4 6" className="route-dash" />
            <text x="255" y="196" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="800" fontFamily="'Plus Jakarta Sans'">SHIPANY</text>
            <text x="255" y="212" textAnchor="middle" fill="#12C8F5" fontSize="9" fontFamily="'JetBrains Mono'">ENGINE</text>
          </svg>
          <div className={`mt-4 flex items-center justify-center gap-2 rounded-xl border px-4 py-3 transition-all duration-500 ${
            recommended >= 0 ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]" : "border-white/10 bg-white/5 text-slate-400"}`}
            data-testid="engine-recommended">
            {recommended >= 0
              ? <><CheckCircle2 className="h-4 w-4" /><span className="font-display text-sm font-bold">RECOMMENDED COURIER: {COURIERS[recommended]}</span></>
              : <><Zap className="h-4 w-4 animate-pulse" /><span className="mono text-xs font-bold tracking-widest">ANALYZING {METRICS[Math.min(metricIdx, METRICS.length - 1)]}…</span></>}
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#FF9933]/40 bg-[#FF9933]/10 px-4 py-1.5 text-xs font-bold tracking-wide text-[#FFB25E]">
            <Package className="h-3.5 w-3.5" /> SMART COURIER ENGINE
          </span>
          <h2 className="font-display mt-5 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            One Shipment. Multiple Choices. <span className="text-saffron-green">One Smart Decision.</span>
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-400 sm:text-base">
            ShipAny helps businesses select the right courier for every shipment instead of manually managing multiple courier accounts. Price, speed, serviceability, delivery performance, COD and RTO risk — scored in real time.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {METRICS.map((m) => (
              <div key={m} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-center">
                <p className="mono text-[10px] font-bold tracking-widest text-slate-300">{m}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
